/**
 * The primitive registry — the seam that makes step bodies pure configuration.
 *
 * A "primitive" is an atomic, reusable step-body building block (a checklist, a scoring grid, a deck
 * viewer, a report editor, …). Each is registered under a stable string key and rendered from a
 * `RunnerContext` plus its own authored `config`. A step's body is an ordered list of primitive
 * references stored in the DB (see `layout/resolveLayout`); the runner looks each key up here and
 * renders it. Adding a new body layout — even a novel one — is then pure config: no code, no new
 * component, as long as the primitives it needs are registered.
 *
 * This module is pure MECHANICS only: it holds the registry map and the register/get/keys functions
 * and imports nothing. The actual built-in primitives are registered by `./core` (which imports the
 * per-body primitive modules); keeping registration out of here means this module — and the
 * mechanics test — never pull the heavy, worker-backed component graph.
 */
import type { RunnerContext } from '../RunnerContext';

/** Author-supplied, primitive-specific configuration (labels, refs, options). Opaque to the registry. */
export type PrimitiveConfig = Record<string, unknown>;

/** Props every primitive receives: the shared runtime context and its own authored config. */
export interface PrimitiveProps {
  ctx: RunnerContext;
  config?: PrimitiveConfig;
}

/** A registered primitive component. */
export type PrimitiveComponent = (props: PrimitiveProps) => JSX.Element | null;

const REGISTRY: Record<string, PrimitiveComponent> = {};

/** Register (or override) a primitive under `key`. Later registration wins, so themes can specialise. */
export function registerPrimitive(key: string, component: PrimitiveComponent): void {
  REGISTRY[key] = component;
}

/** The primitive registered under `key`, or null when the key is unknown. */
export function getPrimitive(key: string): PrimitiveComponent | null {
  return REGISTRY[key] ?? null;
}

/** Every registered primitive key (for admin pickers and validation). */
export function registeredPrimitiveKeys(): string[] {
  return Object.keys(REGISTRY);
}
